/**
 * App notice bus — the styled replacement for window.alert().
 *
 * `notify("message")` can be called from anywhere (components, hooks, plain
 * modules, any workspace package); the author app mounts one NoticeHost that
 * subscribes and renders the queue. Notices raised before the host mounts
 * are buffered and flushed on subscribe.
 */

export type NoticeVariant = "error" | "info";

export interface QpNotice {
	id: number;
	message: string;
	variant: NoticeVariant;
	duration: number;
}

type NoticeListener = (n: QpNotice) => void;

let nextId = 1;
const listeners = new Set<NoticeListener>();
const pending: QpNotice[] = [];

export function notify(
	message: string,
	opts: { variant?: NoticeVariant; duration?: number } = {},
): void {
	const notice: QpNotice = {
		id: nextId++,
		message,
		variant: opts.variant ?? "error",
		duration: opts.duration ?? (opts.variant === "info" ? 5000 : 8000),
	};
	if (listeners.size === 0) {
		pending.push(notice);
		return;
	}
	for (const listener of listeners) listener(notice);
}

/** Subscribe a host; buffered notices are delivered immediately. Returns an
 *  unsubscribe function. */
export function subscribeNotices(listener: NoticeListener): () => void {
	listeners.add(listener);
	while (pending.length > 0) listener(pending.shift() as QpNotice);
	return () => listeners.delete(listener);
}
